import {Badge} from '@mantine/core';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import {MdEdit, MdHome, MdPerson} from 'react-icons/md';
import {routes} from '../../../config/constants';
import {
  BOX_PATH_FEED,
  BOX_PATH_QUESTIONS_ADD,
  BOX_PATH_TOP_ADVISERS,
  BOX_PATH_TOP_QUESTIONS,
} from '../../../core/entities/box/constants';
import {QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE} from '../../../core/entities/login/constants';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {CommonLink} from '../../../shared/components/CommonLink';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {useViewNewQuestionsAt} from '../../corporatePrivacyPolicy/useViewNewQuestionsAt';

type Props = {
  loggedinAdviserName?: string;
};

type NavItem = {
  name: string;
  className: string;
  iconNode: React.ReactNode;
  url: string;
  isQuestionerOnly: boolean;
  isAdviserOnly: boolean;
};

const navList: NavItem[] = [
  {
    name: '相談室トップ',
    className: '',
    iconNode: <MdHome fill="rgba(255, 255, 255, 0.8)" size={25} />,
    url: BOX_PATH_FEED,
    isQuestionerOnly: false,
    isAdviserOnly: false,
  },
  {
    name: '質問する',
    className: '',
    iconNode: <MdEdit fill="rgba(255, 255, 255, 0.8)" size={25} />,
    url: BOX_PATH_QUESTIONS_ADD,
    isQuestionerOnly: true,
    isAdviserOnly: false,
  },
  {
    name: '回答する',
    className: '',
    iconNode: <MdEdit fill="rgba(255, 255, 255, 0.8)" size={25} />,
    url: BOX_PATH_TOP_QUESTIONS,
    isQuestionerOnly: false,
    isAdviserOnly: true,
  },
  {
    name: 'プロフィール',
    className: '',
    iconNode: <MdPerson fill="rgba(255, 255, 255, 0.8)" size={25} />,
    url: BOX_PATH_TOP_ADVISERS,
    isQuestionerOnly: false,
    isAdviserOnly: true,
  },
  {
    name: 'ログイン',
    className: '',
    iconNode: <MdPerson fill="rgba(255, 255, 255, 0.8)" size={25} />,
    url: routes.LOGIN,
    isQuestionerOnly: false,
    isAdviserOnly: false,
  },
];

export const Navigation: React.FC<Props> = ({loggedinAdviserName}) => {
  const isWebView = useIsAppWebView();
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const isAdviser = Boolean(loggedinAdviserName);
  const {isHaveUnViewAdviserQuestion, isHaveUnViewOpenQuestion} =
    useViewNewQuestionsAt();

  return (
    <div className="fixed bottom-0 left-0 z-30 h-[74px] w-full bg-primary-2 pt-[10px]">
      <ul className="flex items-center justify-center">
        {navList.map((item) => {
          if (
            (item.isAdviserOnly && !isAdviser) ||
            (item.isQuestionerOnly && isAdviser) ||
            (item.name === 'ログイン' && (!isWebView || isValid))
          )
            return;

          return (
            <li
              key={item.name}
              className="relative mr-[61px] h-[41px] w-[60px] last:mr-0"
            >
              {item.name === '質問する' || item.name === '回答する' ? (
                <RoundNavItem
                  item={item}
                  isBadgeShown={
                    isHaveUnViewAdviserQuestion || isHaveUnViewOpenQuestion
                  }
                />
              ) : (
                <DefaultNavItem
                  item={item}
                  loggedinAdviserName={loggedinAdviserName}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

function RoundNavItem({
  item,
  isBadgeShown,
}: {
  item: NavItem;
  isBadgeShown: boolean;
}) {
  return (
    <div className="absolute left-0 top-[-22px] h-[72px] w-[72px] rounded-full bg-primary-3 shadow-button">
      <CommonLink
        href={item.url}
        className={
          'flex h-[72px] w-[72px] flex-col items-center justify-center pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
        }
      >
        {item.iconNode}
        <p className="mt-4 text-icon-sp leading-sm text-white">{item.name}</p>
      </CommonLink>
      {item.name === '回答する' && isBadgeShown && <TabBadge />}
    </div>
  );
}

function DefaultNavItem({
  item,
  loggedinAdviserName,
}: {
  item: NavItem;
  loggedinAdviserName: string | undefined;
}) {
  return (
    <CommonLink
      href={createNavPath(item, loggedinAdviserName)}
      className={
        'flex flex-col items-center pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out'
      }
    >
      {item.iconNode}
      <p className="mt-4 text-icon-sp leading-sm text-white">{item.name}</p>
    </CommonLink>
  );
}

function createNavPath(
  item: NavItem,
  loggedinAdviserName: string | undefined,
): string {
  const router = useRouter();

  if (Boolean(loggedinAdviserName) && item.name === 'プロフィール') {
    return `${item.url}/${loggedinAdviserName}`;
  }

  if (item.name === 'ログイン') {
    return `${item.url}?${QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE}=${router.asPath}`;
  }

  return item.url;
}

function TabBadge() {
  return (
    <Badge
      sx={{width: 10, height: 10, pointerEvents: 'none'}}
      variant="filled"
      size="xs"
      p={0}
      styles={() => ({
        root: {
          backgroundColor: '#F44336',
          position: 'absolute',
          top: 12,
          right: 0,
        },
      })}
    />
  );
}
